import React, { useState, useEffect } from 'react';
import axios from 'axios';
import DataTable from './DataTable';
import Alert from './Alert';
import { useAlert } from '../hooks/useAlert';

const API_URL = '/api';

function AssetTypes() {
  const [assetTypes, setAssetTypes] = useState([]);
  const [formData, setFormData] = useState({ name: '', description: '' });
  const [editingId, setEditingId] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [quickAddName, setQuickAddName] = useState('');
  const { alert, showAlert, hideAlert } = useAlert();

  useEffect(() => {
    fetchAssetTypes();
    document.title = 'Asset Types - Saral Asset Manager';
  }, []);

  const fetchAssetTypes = async () => {
    try {
      const response = await axios.get(`${API_URL}/assettypes`);
      setAssetTypes(response.data);
    } catch (error) {
      console.error('Error fetching asset types:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await axios.put(`${API_URL}/assettypes/${editingId}`, { ...formData, id: editingId });
        handleCloseModal();
        showAlert('success', 'Asset type updated successfully!');
      } else {
        await axios.post(`${API_URL}/assettypes`, formData);
        handleCloseModal();
        showAlert('success', 'Asset type added successfully!');
      }
      fetchAssetTypes();
    } catch (error) {
      if (error.response?.status === 409) {
        showAlert('danger', 'Asset type with this name already exists!');
      } else {
        showAlert('danger', 'Error saving asset type');
      }
    }
  };

  const handleQuickAdd = async (e) => {
    e.preventDefault();
    if (!quickAddName.trim()) return;
    try {
      await axios.post(`${API_URL}/assettypes`, {
        name: quickAddName.trim(),
        description: `${quickAddName.trim()} for office use`
      });
      setQuickAddName('');
      showAlert('success', 'Asset type added successfully!');
      fetchAssetTypes();
    } catch (error) {
      if (error.response?.status === 409) {
        showAlert('danger', 'Asset type with this name already exists!');
      } else {
        showAlert('danger', 'Error adding asset type');
      }
    }
  };

  const handleEdit = (assetType) => {
    setFormData({ name: assetType.name, description: assetType.description });
    setEditingId(assetType.id);
    setShowModal(true);
  };

  const handleAdd = () => {
    setFormData({ name: '', description: '' });
    setEditingId(null);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setFormData({ name: '', description: '' });
    setEditingId(null);
  };

  const handleDelete = (id) => {
    setDeleteId(id);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    try {
      await axios.delete(`${API_URL}/assettypes/${deleteId}`);
      setShowDeleteModal(false);
      setDeleteId(null);
      showAlert('success', 'Asset type deleted successfully!');
      fetchAssetTypes();
    } catch (error) {
      showAlert('danger', 'Error deleting asset type');
    }
  };

  const columns = [
    { field: 'name', label: 'Name' },
    { field: 'description', label: 'Description' },
    { 
      field: 'createdAt', 
      label: 'Created At',
      render: (value) => new Date(value).toLocaleDateString()
    }
  ];

  return (
    <div className="container-fluid">
      <Alert show={alert.show} type={alert.type} message={alert.message} onClose={hideAlert} />
      <div className="mb-3">
        <h1 className="page-title mb-0">Asset Types</h1>
      </div>

      <div className="card border-0 shadow-sm mb-3">
        <div className="card-header bg-light">
          <h6 className="mb-0"><i className="fas fa-plus-circle me-2"></i>Quick Add</h6>
        </div>
        <div className="card-body">
          <form onSubmit={handleQuickAdd} className="row g-2">
            <div className="col-md-8">
              <input
                type="text"
                className="form-control"
                placeholder="Enter asset type name (e.g., Laptop, Chair, etc.)"
                value={quickAddName}
                onChange={(e) => setQuickAddName(e.target.value)}
              />
            </div>
            <div className="col-md-4">
              <button type="submit" className="btn btn-success w-100" disabled={!quickAddName.trim()}>
                <i className="fas fa-plus me-1"></i>Add
              </button>
            </div>
          </form>
        </div>
      </div>

      <div className="card border-0 shadow-sm">
        <div className="card-body">
          <DataTable
            data={assetTypes}
            columns={columns}
            onEdit={handleEdit}
            onDelete={handleDelete}
          />
        </div>
      </div>

      {/* Modal */}
      <div className={`modal fade ${showModal ? 'show d-block' : ''}`} style={{backgroundColor: showModal ? 'rgba(0,0,0,0.5)' : 'transparent'}}>
        <div className="modal-dialog">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">
                <i className="fas fa-tags me-2"></i>
                {editingId ? 'Edit Asset Type' : 'Add New Asset Type'}
              </h5>
              <button type="button" className="btn-close" onClick={handleCloseModal}></button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label">Name</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Enter asset type name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">Description</label>
                  <textarea
                    className="form-control"
                    rows="3"
                    placeholder="Enter description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  ></textarea>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={handleCloseModal}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  <i className="fas fa-save me-1"></i>
                  {editingId ? 'Update' : 'Add'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <div className={`modal fade ${showDeleteModal ? 'show d-block' : ''}`} style={{backgroundColor: showDeleteModal ? 'rgba(0,0,0,0.5)' : 'transparent'}}>
        <div className="modal-dialog">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">
                <i className="fas fa-exclamation-triangle me-2 text-warning"></i>
                Confirm Delete
              </h5>
              <button type="button" className="btn-close" onClick={() => setShowDeleteModal(false)}></button>
            </div>
            <div className="modal-body">
              Are you sure you want to delete this asset type? This action cannot be undone.
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={() => setShowDeleteModal(false)}>
                Cancel
              </button>
              <button type="button" className="btn btn-danger" onClick={confirmDelete}>
                <i className="fas fa-trash me-1"></i>
                Delete
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AssetTypes;